import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dashboardService from "./services/dashboardService";

const NAVY = "#004A78";
const COLORS = ["#004A78", "#2E7D32", "#F9A825", "#6A1B9A", "#00838F", "#C62828"];
const DASHBOARD_BRANCHES = ["NE", "SP", "PA"];
const BRANCH_COLORS: Record<string, string> = {
  NE: "#004A78",
  SP: "#2E7D32",
  PA: "#F9A825",
};

const MONTHS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const YEARS = Array.from({ length: new Date().getFullYear() - 2019 }, (_, index) => 2020 + index);

const money = (value: number) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const pairs = (value: number) =>
  Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 });

const percent = (value: number | null) =>
  value == null
    ? "—"
    : `${(value * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

const deltaLabel = (value: number | null) => {
  if (value == null) return "—";
  const sign = value > 0 ? "↑" : value < 0 ? "↓" : "";
  return `${sign} ${percent(value)}`;
};

const ChartCard = ({ title, children }: { title: string; children: any }) => (
  <Card sx={{ p: 2, height: 320, display: "flex", flexDirection: "column" }}>
    <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
  </Card>
);

const MonthSelect = ({
  label,
  month,
  year,
  onMonth,
  onYear,
}: {
  label: string;
  month: number;
  year: number;
  onMonth: (value: number) => void;
  onYear: (value: number) => void;
}) => (
  <Stack direction="row" spacing={1}>
    <FormControl size="small" sx={{ minWidth: 130 }}>
      <InputLabel>{label}</InputLabel>
      <Select value={month} label={label} onChange={(event) => onMonth(Number(event.target.value))}>
        {MONTHS.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl size="small" sx={{ minWidth: 90 }}>
      <InputLabel>Ano</InputLabel>
      <Select value={year} label="Ano" onChange={(event) => onYear(Number(event.target.value))}>
        {YEARS.map((item) => (
          <MenuItem key={item} value={item}>
            {item}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Stack>
);

export default function ExecutiveDashboard() {
  const now = new Date();
  const [mode, setMode] = useState<"month" | "range">("month");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [fromMonth, setFromMonth] = useState(1);
  const [fromYear, setFromYear] = useState(2020);
  const [toMonth, setToMonth] = useState(now.getMonth() + 1);
  const [toYear, setToYear] = useState(now.getFullYear());
  const [boundsReady, setBoundsReady] = useState(false);
  const [branch, setBranch] = useState("Todas");
  const [customerMode, setCustomerMode] = useState<"all" | "postpaid" | "prepaid">("all");
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [data, setData] = useState<any>(null);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getExecutive(
        mode === "range"
          ? {
              mode: "range",
              fromMonth: boundsReady ? fromMonth : undefined,
              fromYear: boundsReady ? fromYear : undefined,
              toMonth: boundsReady ? toMonth : undefined,
              toYear: boundsReady ? toYear : undefined,
              branch,
            }
          : { mode: "month", month, year, branch },
      );
      const payload = response.data;
      setData(payload);
      if (payload?.period?.mode === "range") {
        setFromMonth(payload.period.fromMonth);
        setFromYear(payload.period.fromYear);
        setToMonth(payload.period.toMonth);
        setToYear(payload.period.toYear);
        setBoundsReady(true);
      } else if (payload?.period) {
        setMonth(payload.period.month);
        setYear(payload.period.year);
      }
    } catch (error) {
      console.error(error);
      setData(null);
    } finally {
      setLoading(false);
      setReady(true);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const kpis = data?.kpis || {};
  const openOrders = data?.openOrders || {};
  const openTotal = DASHBOARD_BRANCHES.reduce(
    (sum, item) => sum + Number(openOrders[item] || 0),
    0,
  );

  const openChart = DASHBOARD_BRANCHES.map((item) => ({
    branch: item,
    billed: Number(openOrders[item] || 0),
  }));

  const selectedOrderTypes = customerMode === "all" ? data?.allOrderTypes : data?.orderTypes;
  const orderTypes = [
    { name: "Venda", pairs: Number(selectedOrderTypes?.salePairs || 0) },
    { name: "Cortesia", pairs: Number(selectedOrderTypes?.courtesyPairs || 0) },
  ];
  const isRange = (data?.period?.mode || mode) === "range";
  const series = data?.monthlySeries || [];
  const tickInterval = series.length > 16 ? Math.floor(series.length / 12) : 0;
  const scope = isRange ? "período" : "mês";
  const deltaHint = (value: number | null) =>
    isRange ? `${deltaLabel(value)} · 1º × último mês` : deltaLabel(value);
  const prepaid = data?.prepaid || {};
  const prepaidKpis = prepaid.kpis || {};
  const prepaidByMonth = new Map((prepaid.monthlySeries || []).map((item: any) => [item.month, item]));
  const generalSeries = series.map((item: any) => {
    const pre: any = prepaidByMonth.get(item.month) || {};
    return {
      month: item.month,
      ...DASHBOARD_BRANCHES.reduce(
        (values, unit) => ({ ...values, [unit]: Number(item[unit] || 0) + Number(pre[unit] || 0) }),
        {},
      ),
    };
  });
  const prepaidMix = new Map((prepaid.arMix || []).map((item: any) => [item.key, item]));
  const generalArMix = (data?.arMix || []).map((item: any) => {
    const pre: any = prepaidMix.get(item.key) || {};
    return { ...item, pairs: Number(item.pairs || 0) + Number(pre.pairs || 0), billed: Number(item.billed || 0) + Number(pre.billed || 0) };
  });
  const prepaidBranches = new Map((prepaid.branches || []).map((item: any) => [item.branch, item]));
  const generalBranches = (data?.branches || []).map((item: any) => {
    const pre: any = prepaidBranches.get(item.branch) || {};
    return { ...item, pairs: Number(item.pairs || 0) + Number(pre.pairs || 0), billed: Number(item.billed || 0) + Number(pre.consumed || 0) };
  });
  const generalCustomers = data?.generalTopCustomers || [];
  const prepaidPremiumByMonth = new Map((prepaid.premiumSeries || []).map((item: any) => [item.month, item]));
  const generalPremiumSeries = (data?.premiumSeries || []).map((item: any) => {
    const pre: any = prepaidPremiumByMonth.get(item.month) || {};
    const arPairs = Number(item.arPairs || 0) + Number(pre.arPairs || 0);
    const premiumPairs = Number(item.premiumPairs || 0) + Number(pre.premiumPairs || 0);
    return { month: item.month, premiumShare: arPairs > 0 ? premiumPairs / arPairs : 0 };
  });
  const displayedSeries = customerMode === "all" ? generalSeries : series;
  const displayedArMix = customerMode === "all" ? generalArMix : data?.arMix || [];
  const displayedBranches = customerMode === "all" ? generalBranches : data?.branches || [];
  const displayedCustomers = customerMode === "all" ? generalCustomers : data?.topCustomers || [];
  const displayedPremiumSeries = customerMode === "all" ? generalPremiumSeries : data?.premiumSeries || [];
  const generalLabel = customerMode === "all" ? "Geral" : "Pós-pago";
  const dashboardKpis =
    customerMode === "prepaid"
      ? [
          ["Saldo pré-pago", money(prepaidKpis.currentBalance), "Saldo ao fim do período"],
          ["Créditos adicionados", money(prepaidKpis.creditsAdded), `No ${scope}`],
          ["Consumo", money(prepaidKpis.consumed), `Débitos de OS no ${scope}`],
          ["Volume", `${pairs(prepaidKpis.pairs)} pares`, "Associados aos débitos"],
          ["Clientes ativos", pairs(prepaidKpis.activeCustomers), "Com consumo no período"],
        ]
      : customerMode === "all"
        ? [
            ["Movimento comercial", money(Number(kpis.billed || 0) + Number(prepaidKpis.consumed || 0)), "Pós faturado + pré consumido"],
            ["Pós-pago faturado", money(kpis.billed), deltaHint(kpis.billedDelta)],
            ["Pré-pago consumido", money(prepaidKpis.consumed), `No ${scope}`],
            ["Saldo pré-pago", money(prepaidKpis.currentBalance), "Saldo ao fim do período"],
            ["Carteira pós-paga", money(openTotal), "OS sem fechamento"],
          ]
        : [
            ["Faturado", money(kpis.billed), deltaHint(kpis.billedDelta)],
            ["Volume", `${pairs(kpis.pairs)} pares`, deltaHint(kpis.pairsDelta)],
            ["Ticket médio", money(kpis.averageTicket), ""],
            ["Premium", percent(kpis.premiumShare), "AR fora de Class/Extra Clean"],
            ["Carteira aberta", money(openTotal), "OS pós-pagas sem fechamento"],
          ];

  return (
    <Box>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        alignItems={{ lg: "center" }}
        spacing={1.25}
        sx={{ mb: 1.5, flexWrap: "wrap" }}
      >
        <Typography variant="h6" sx={{ color: NAVY, fontWeight: 700, mr: 1 }}>
          Visão executiva
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_event, value) => value && setMode(value)}
        >
          <ToggleButton value="month">Mês específico</ToggleButton>
          <ToggleButton value="range">Visão geral</ToggleButton>
        </ToggleButtonGroup>
        {mode === "month" ? (
          <MonthSelect label="Mês" month={month} year={year} onMonth={setMonth} onYear={setYear} />
        ) : (
          <>
            <MonthSelect label="De" month={fromMonth} year={fromYear} onMonth={setFromMonth} onYear={setFromYear} />
            <MonthSelect label="Até" month={toMonth} year={toYear} onMonth={setToMonth} onYear={setToYear} />
          </>
        )}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Unidade</InputLabel>
          <Select value={branch} label="Unidade" onChange={(event) => setBranch(event.target.value)}>
            <MenuItem value="Todas">Todas</MenuItem>
            {DASHBOARD_BRANCHES.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={customerMode}
          onChange={(_event, value) => value && setCustomerMode(value)}
          aria-label="Regime do cliente"
        >
          <ToggleButton value="all">Todos</ToggleButton>
          <ToggleButton value="postpaid">Pós-pago</ToggleButton>
          <ToggleButton value="prepaid">Pré-pago</ToggleButton>
        </ToggleButtonGroup>
        <Button
          size="small"
          variant="contained"
          disabled={loading}
          onClick={fetchDashboard}
          sx={{ height: 36, backgroundColor: NAVY, "&:hover": { backgroundColor: "#226C9A" } }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : "Atualizar"}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {data?.period?.label || ""}
        </Typography>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {dashboardKpis.map(([label, value, hint]) => (
          <Grid item xs={12} sm={6} md={2.4} key={String(label)}>
            <Card sx={{ px: 2, py: 1.25, height: "100%" }}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="h6" sx={{ color: NAVY, fontWeight: 800, lineHeight: 1.2 }}>
                {ready ? value : "—"}
              </Typography>
              <Typography variant="caption" sx={{ color: String(hint).includes("↓") ? "#C62828" : "#2E7D32" }}>
                {hint}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {customerMode === "prepaid" && (
        <Grid container spacing={1.5}>
          <Grid item xs={12} lg={8}>
            <ChartCard title={`Pré-pago — créditos e consumo no ${scope}`}>
              <ResponsiveContainer>
                <BarChart data={prepaid.monthlySeries || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} interval={tickInterval} />
                  <YAxis tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any) => money(Number(value))} />
                  <Legend />
                  <Bar dataKey="creditsAdded" name="Créditos adicionados" fill="#2E7D32" />
                  <Bar dataKey="refunds" name="Estornos" fill="#90A4AE" />
                  <Bar dataKey="consumed" name="Consumo" fill="#F9A825" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} lg={4}>
            <ChartCard title="Pré-pago — consumo por unidade">
              <ResponsiveContainer>
                <BarChart data={prepaid.branches || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="branch" />
                  <YAxis tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                  <Tooltip formatter={(value: any, name) => name === "Consumo" ? money(Number(value)) : `${pairs(Number(value))} pares`} />
                  <Legend />
                  <Bar dataKey="consumed" name="Consumo" fill="#00838F" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12}>
            <ChartCard title="Top 10 clientes pré-pagos por consumo">
              <ResponsiveContainer>
                <BarChart data={prepaid.topCustomers || []} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                  <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any, name) => name === "Consumo" ? money(Number(value)) : `${pairs(Number(value))} pares`} />
                  <Bar dataKey="consumed" name="Consumo" fill="#004A78" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}

      {customerMode !== "prepaid" && <Grid container spacing={1.5}>
        <Grid item xs={12} lg={8}>
          <ChartCard title={isRange ? `${generalLabel} — faturamento no período` : `${generalLabel} — faturamento em 12 meses`}>
            <ResponsiveContainer>
              <BarChart data={displayedSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} interval={tickInterval} />
                <YAxis tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value: any) => money(Number(value))} />
                <Legend />
                {DASHBOARD_BRANCHES.map((item) => (
                  <Bar key={item} dataKey={item} stackId="a" fill={BRANCH_COLORS[item]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} lg={4}>
          <ChartCard title={`${generalLabel} — mix AR do ${scope}`}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={displayedArMix.filter((item: any) => item.pairs > 0)}
                  dataKey="pairs"
                  nameKey="label"
                  innerRadius={50}
                  outerRadius={80}
                >
                  {displayedArMix.map((item: any, index: number) => (
                    <Cell key={item.key} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => `${pairs(Number(value))} pares`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title={`${generalLabel} — filiais no ${scope}`}>
            <ResponsiveContainer>
              <BarChart
                data={displayedBranches.filter((item: any) =>
                  DASHBOARD_BRANCHES.includes(item.branch),
                )}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="branch" />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="pairs" name="Pares" fill="#004A78" />
                <Bar yAxisId="right" dataKey="billed" name="Faturado" fill="#F9A825" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title={`Top 10 clientes — ${generalLabel.toLowerCase()}`}>
            <ResponsiveContainer>
              <BarChart
                data={displayedCustomers}
                layout="vertical"
                margin={{ left: 24 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value: any, name) => (name === "billed" ? money(Number(value)) : pairs(Number(value)))} />
                <Bar dataKey="billed" name="Faturado" fill="#004A78" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Carteira pós-paga em aberto">
            <ResponsiveContainer>
              <BarChart data={openChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="branch" />
                <YAxis tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                <Tooltip formatter={(value: any) => money(Number(value))} />
                <Bar dataKey="billed" name="Aberto" fill="#C62828" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title={isRange ? `% premium ${generalLabel.toLowerCase()} — período` : `% premium ${generalLabel.toLowerCase()} — 12 meses`}>
            <ResponsiveContainer>
              <LineChart data={displayedPremiumSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} interval={tickInterval} />
                <YAxis tickFormatter={(value) => `${Math.round(Number(value) * 100)}%`} domain={[0, 1]} />
                <Tooltip formatter={(value: any) => percent(Number(value))} />
                <Line type="monotone" dataKey="premiumShare" name="Premium" stroke="#2E7D32" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title={`Venda vs cortesia (pares no ${scope})`}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={orderTypes} dataKey="pairs" nameKey="name" innerRadius={50} outerRadius={80}>
                  <Cell fill="#004A78" />
                  <Cell fill="#90A4AE" />
                </Pie>
                <Tooltip formatter={(value: any) => `${pairs(Number(value))} pares`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>}
    </Box>
  );
}
